import React, { useEffect, useState, useCallback } from 'react';
import { auth } from '@/lib/picasso-firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "admin" | "user" | ("admin" | "user")[];
  publicRoute?: boolean; // New prop for public routes like login
}

// Helper function to handle authenticated user redirection in public routes
const getAuthenticatedUserRedirect = (userRole: string | null, userStatus: string | null): React.ReactElement | null => {
  if (userRole === "admin" && userStatus === "approved") {
    return <Navigate to="/dashboard" replace />;
  }

  if (userRole === "user" && userStatus === "approved") {
    return <Navigate to="/dashboard" replace />;
  }

  if (userStatus === "pending") {
    return <Navigate to="/login" replace />;
  }

  return null; // No redirection needed
};

// Helper function to handle role-based access control
const getRoleBasedRedirect = (
  userRole: string | null,
  userStatus: string | null,
  requiredRole?: "admin" | "user" | ("admin" | "user")[]
): React.ReactElement | null => {
  if (!requiredRole) {
    if (!userRole || userStatus !== "approved") {
      return <Navigate to="/login" replace />;
    }
    return null;
  }

  const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  const hasRequiredRole = allowedRoles.includes(userRole as "admin" | "user");

  if (!hasRequiredRole || userStatus !== "approved") {
    if (userRole === "admin" && userStatus === "approved") {
      return <Navigate to="/dashboard" replace />;
    } else if (userRole === "user" && userStatus === "approved") {
      return <Navigate to="/dashboard" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  return null;
};

// Helper function to render loading state
const renderLoadingState = (): React.ReactElement => (
  <div className="fixed inset-0 bg-white flex items-center justify-center">
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
        <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Helper function to render pending approval state
const renderPendingState = (): React.ReactElement => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Pending Approval</h2>
      <p className="text-gray-600 mb-4">
        Your account is pending approval by an admin. You will be notified once approved.
      </p>
      <button
        onClick={() => auth.signOut()}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
      >
        Sign Out
      </button>
    </div>
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  publicRoute = false, // Default to protected route
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserData = useCallback(async (user: any) => {
    try {
      // Use email for lookup as Firebase UID might not match backend UUID or be a valid UUID
      const identifier = user.email || user.uid;
      
      const response = await fetch(`${import.meta.env.VITE_BASE_URL}/picasso/auth/user/${identifier}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        cache: 'no-store',
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // Handle the case where user is not found in database
        if (userData.status === "not_found") {
          setUserRole(null);
          setUserStatus("not_found");
        } else {
          setUserRole(userData.role);
          setUserStatus(userData.status);
        }
      } else {
        setUserRole(null);
        setUserStatus(null);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserRole(null);
      setUserStatus(null);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAuthenticated(false);
        setUserRole(null);
        setUserStatus(null);
        setIsLoading(false);
        return;
      }

      setIsAuthenticated(true);
      await fetchUserData(user);
    });

    return () => unsubscribe();
  }, [fetchUserData]);

  if (isLoading) {
    return renderLoadingState();
  }

  // Handle public routes (like login page)
  if (publicRoute) {
    // If user is not authenticated, allow access to public page
    if (!isAuthenticated) {
      return <>{children}</>;
    }

    // User is authenticated, redirect them to appropriate dashboard
    const redirect = getAuthenticatedUserRedirect(userRole, userStatus);
    if (redirect) {
      return redirect;
    }

    // Handle not found users or other cases - allow them to stay on login
    if (userStatus === "not_found" || !userRole || !userStatus) {
      return <>{children}</>;
    }

    // Default case: redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  // Continue with original protected route logic
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If API fails and we don't have user data, redirect to login
  if (userRole === null && userStatus === null) {
    return <Navigate to="/login" replace />;
  }

  // Handle users not found in database (new users) - redirect to login
  if (userStatus === "not_found") {
    return <Navigate to="/login" replace />;
  }

  if (userStatus === "pending") {
    return renderPendingState();
  }

  // Handle role-based access control
  const roleRedirect = getRoleBasedRedirect(userRole, userStatus, requiredRole);
  if (roleRedirect) {
    return roleRedirect;
  }

  // Clone children and inject user props
  const currentUser = auth.currentUser;
  const userEmail = currentUser?.email || null;
  const isAdmin = userRole === "admin";
  
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      // Create new props object with user data using object spread
      const newProps = {
        ...(child.props as object),
        userEmail,
        isAdmin,
      };
      
      return React.cloneElement(child, newProps);
    }
    return child;
  });

  return <>{childrenWithProps}</>;
};

export default ProtectedRoute;
